import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import salutioLogo from '../../images/salutioRed.png'; // Logo
import backgroundImage from '../../images/background2.png'; // Nueva imagen de fondo
import { useDataUser } from '../../context/dataUserContext';
import '../../styles/Login.css';
import { alertaGeneral } from '../../utils/alertas';
import apiClient from '../../utils/axiosPetition';
import { ReactComponent as Contraseña } from '../../assets/icons/contraseña.svg';
import { ReactComponent as Usuario } from '../../assets/icons/usuario.svg';
import { ReactComponent as FlechaIngresar } from '../../assets/icons/ingresar.svg';
import SvgIcon from '../../utils/Icon';

Modal.setAppElement('#root'); // Para accesibilidad del modal

const Login = ({ callback }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPrestadorOptions, setShowPrestadorOptions] = useState(false);
  const [showUsuarioOptions, setShowUsuarioOptions] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordResetStep, setPasswordResetStep] = useState(1); // 1: email, 2: código, 3: nueva contraseña
  const isShowRegistro = useLocation().state?.showRegistro || false;
  const [showModalRegistro, setShowModalRegistro] = useState(isShowRegistro);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginSuccess, setShowLoginSuccess] = useState(false);

  const { isAuth, role, setIsAuth } = useDataUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [showOAuthMessage, setShowOAuthMessage] = useState(false);
  const [oauthEmail, setOauthEmail] = useState('');

  /* Por si ya está logeado, que navege a su respectiva ruta */
  useEffect(() => {
    if (isAuth) {
      navigate(`/${role}`);
    } else {
      localStorage.clear();
      setIsAuth(false);
    }
  }, [])

  // Detectar si viene de OAuth con error de usuario no registrado
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const oauthError = searchParams.get('oauth_error');
    const email = searchParams.get('email');
    
    if (oauthError === 'user_not_registered') {
      setOauthEmail(email || '');
      setShowOAuthMessage(true);
      // Limpiar los parámetros de la URL
      navigate('/login', { replace: true });
      
      // Mostrar alerta
      alertaGeneral({
        titulo: 'Usuario no registrado',
        messageHtml: email 
          ? `El correo ${email} no está registrado. Por favor, regístrate primero.`
          : 'Este usuario no está registrado. Por favor, regístrate primero.',
        isWarning: true
      });

      // Hacer scroll hacia el botón de registrarse después de un pequeño delay
      setTimeout(() => {
        const botonRegistrarse = document.getElementById('boton-registrarse');
        if (botonRegistrarse) {
          botonRegistrarse.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Agregar un efecto de parpadeo
          botonRegistrarse.classList.add('animate-pulse');
          setTimeout(() => {
            botonRegistrarse.classList.remove('animate-pulse');
          }, 3000);
        }
      }, 500);
    }
  }, [location.search, navigate]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    setEmailVerified(false);
    setEmailMessage('');
    setPassword('');
  }, [username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailVerified) return;
    setIsLoading(true);
    try {
      const response = await apiClient.post(`/api/auth/login`, {
        username,
        password,
      });
      if (response.data) {
        setIsLoading(false);
        setShowLoginSuccess(true);
        setTimeout(() => {
          navigate('/code-verification', { state: { email: username, render: 'login' } });
        }, 900);
      } else {
        setIsLoading(false);
        alertaGeneral({
          titulo: "Error",
          messageHtml: "Ocurrió un error inesperado, intente de nuevo",
          isError: true
        });
      }
    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 403) {
        alertaGeneral({
          titulo: "Cuenta desactivada",
          messageHtml: "Tu cuenta ha sido desactivada. Contacta al administrador.",
          isWarning: true
        });
        return;
      }
      alertaGeneral({
        titulo: error.response?.data?.message || "Ocurrió un error inesperado, intente de nuevo",
        isError: true
      });
    }
  }

  const handleVerifyEmail = async () => {
    if (!username) return;
    setIsVerifyingEmail(true);
    setEmailMessage('');
    try {
      const res = await apiClient.post('/api/auth/check-email', { email: username });
      if (res.data?.exists) {
        setEmailVerified(true);
        setEmailMessage('');
      } else {
        setEmailMessage(res.data?.message || 'No encontramos este correo.');
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        setEmailMessage('No encontramos este correo. Regístrate para continuar.');
      } else if (status === 403) {
        setEmailMessage(error.response?.data?.message || 'La cuenta está desactivada.');
      } else {
        setEmailMessage('Error al verificar el correo. Intenta de nuevo.');
      }
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const handlePasswordResetRequest = async () => {
    if (!email) {
      alertaGeneral({
        titulo: "Error",
        messageHtml: "Por favor, ingresa un correo electrónico.",
        isError: true
      });
      return;
    }
    apiClient.post(`/api/auth/reset-password-request`, { email })
      .then((res) => {
        alertaGeneral({
          titulo: "Código enviado",
          messageHtml: "Te hemos enviado un código de verificación a tu correo electrónico.",
        });
        setPasswordResetStep(2); // Avanzar al paso 2 (ingresar código)
      })
      .catch((error) => {
        alertaGeneral({
          titulo: "Error",
          messageHtml: "Hubo un problema al enviar el código. Por favor, intenta de nuevo.",
          isError: true
        });
      })
  }

  const handleVerifyCode = () => {
    if (!verificationCode) {
      alertaGeneral({
        titulo: "Error",
        messageHtml: "Por favor, ingresa el código de verificación.",
        isError: true
      });
      return;
    }
    // Avanzar al paso 3 (nueva contraseña)
    setPasswordResetStep(3);
  }

  const handlePasswordReset = async () => {
    if (!verificationCode || !newPassword) {
      alertaGeneral({
        titulo: "Error",
        messageHtml: "El código de verificación y la nueva contraseña son obligatorios.",
        isError: true
      });
      return;
    }
    apiClient.post(`/api/auth/reset-password`, {
      email,
      verificationCode,
      newPassword,
    })
      .then((res) => {
        alertaGeneral({
          titulo: "Contraseña cambiada",
          messageHtml: "Tu contraseña ha sido cambiada exitosamente.",
        });
        // Resetear el modal
        setIsModalOpen(false);
        setPasswordResetStep(1);
        setEmail('');
        setVerificationCode('');
        setNewPassword('');
      })
      .catch((error) => {
        alertaGeneral({
          titulo: "Error",
          messageHtml: "Hubo un problema al cambiar la contraseña. Verifica el código e intenta de nuevo.",
          isError: true
        });
      })
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Resetear al cerrar
    setPasswordResetStep(1);
    setEmail('');
    setVerificationCode('');
    setNewPassword('');
  };

  const handleSocialLogin = async (provider) => {
    try {
      // Redirigir a la ruta de OAuth del backend (flow=login para no crear usuario si no existe)
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const oauthUrl = `${baseUrl}/api/auth/${provider}?flow=login`;
      
      // Redirigir a OAuth
      window.location.href = oauthUrl;
    } catch (error) {
      console.error('Error en handleSocialLogin:', error);
      alertaGeneral({
        titulo: 'Error',
        messageHtml: `Error al iniciar sesión con ${provider === 'google' ? 'Google' : 'LinkedIn'}. Por favor, intenta nuevamente.`,
        isError: true
      });
    }
  };

  return (
    <div
      className="flex items-center mx-auto my-auto justify-center min-h-screen bg-cover bg-center"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Overlay de carga al iniciar sesión */}
      {isLoading && (
        <div className="login-loading-overlay" aria-live="polite" aria-busy="true">
          <div className="login-loading-content">
            <div className="login-loading-spinner-wrap">
              <div className="login-loading-spinner" />
              <div className="login-loading-spinner-inner" />
            </div>
            <p className="login-loading-text">Verificando credenciales...</p>
          </div>
        </div>
      )}

      {/* Overlay de éxito antes de redirigir */}
      {showLoginSuccess && (
        <div className="login-success-overlay" aria-live="polite">
          <div className="login-success-content">
            <div className="login-success-circle">
              <div className="login-success-checkmark" />
            </div>
            <p className="login-success-text">¡Bienvenido!</p>
          </div>
        </div>
      )}

      <div className="w-full mx-auto max-w-[540px] min-h-screen bg-white bg-opacity-55">
        <div className="relative group animate-fade-in" onClick={() => navigate('/')}>
          <img src={salutioLogo} alt="Logo" className="cursor-pointer max-w-[18em] min-w-[15em] mt-[3em] mx-auto mb-[2em] transition-transform duration-300 hover:scale-105" />
          <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-max bg-[#f79865ff] text-white text-xs rounded p-1 opacity-0 group-hover:opacity-100 transition-all duration-300">
            Toque para ir a la página principal
          </span>
        </div>
        <div className="justify-center align-middle items-center">
          <p className="mb-3 mx-auto text-center text-xs text-[#8a8a88ff] w-[210px] animate-slide-up">Ingresa usuario y contraseña para iniciar sesión en tú cuenta</p>

          {/* Botones de Social Login */}
          <div className="w-[270px] mx-auto mb-3 space-y-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <button
              type="button"
              onClick={() => handleSocialLogin('google')}
              className="w-full flex items-center justify-center px-4 py-2.5 border border-[#dfe6ef] rounded-lg bg-white text-[#1f2d3d] hover:bg-[#f5f8fb] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-sm font-medium"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Iniciar sesión con Google
            </button>
            <button
              type="button"
              onClick={() => handleSocialLogin('linkedin')}
              className="w-full flex items-center justify-center px-4 py-2.5 border border-[#dfe6ef] rounded-lg bg-white text-[#1f2d3d] hover:bg-[#f5f8fb] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-sm font-medium"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#0077B5">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              Iniciar sesión con LinkedIn
            </button>
          </div>

          <div className="relative mb-3 w-[270px] mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white bg-opacity-70 text-gray-600 font-medium">O continuar con correo</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="w-[270px] mx-auto animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <div className="mb-3">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1.5">
                Correo electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SvgIcon Icon={Usuario} className="w-5 h-5 text-[#1f94c2ff]" />
                </div>
                <input
                  type="email"
                  id="username"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  required
                  placeholder="tu@correo.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff] focus:border-transparent transition duration-200 shadow-sm"
                />
              </div>
              {emailMessage && (
                <p className="mt-2 text-xs text-gray-600">{emailMessage}</p>
              )}
            </div>

            {emailVerified && (
              <div className="mb-3 animate-slide-up">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SvgIcon Icon={Contraseña} className="w-5 h-5 text-[#1f94c2ff]" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff] focus:border-transparent transition duration-200 shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center">
              {!emailVerified ? (
                <button
                  type="button"
                  onClick={handleVerifyEmail}
                  disabled={isVerifyingEmail || !username}
                  className="w-full flex items-center justify-center gap-2 bg-[#1f94c2ff] text-white border border-[#1f94c2ff] hover:bg-[#1a7fa8] hover:scale-[1.02] active:scale-[0.98] font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 shadow-md focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isVerifyingEmail && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#1f94c2ff] border-t-transparent"></div>
                  )}
                  <span>{isVerifyingEmail ? 'Verificando...' : 'Continuar'}</span>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 bg-[#1f94c2ff] hover:bg-[#1a7fa8] hover:scale-[1.02] active:scale-[0.98] text-white font-semibold px-4 py-2.5 rounded-lg transition-all duration-200 shadow-md focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <SvgIcon Icon={FlechaIngresar} className="w-4 h-4" />
                  <span>Iniciar sesión</span>
                </button>
              )}
            </div>

            {/* Mensaje cuando viene de OAuth sin estar registrado */}
            {showOAuthMessage && (
              <div className="mb-3 p-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg animate-pulse">
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-yellow-600 text-lg">👉</span>
                  <p className="text-xs font-semibold text-yellow-800 text-center">
                    No estás registrado. Haz clic en <strong>"Registrarse"</strong> para crear tu cuenta.
                  </p>
                </div>
              </div>
            )}

            <div className={`flex justify-between mt-3 mb-3 text-[#1f94c2ff] ${showOAuthMessage ? 'relative' : ''}`}>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="text-xs font-semibold hover:underline hover:text-[#1a7fa8] focus:outline-none transition-colors duration-200"
              >
                ¿Olvidaste tu contraseña?
              </button>
              <button
                type="button"
                id="boton-registrarse"
                className={`text-xs font-semibold hover:underline hover:text-[#1a7fa8] focus:outline-none transition-all duration-200 ${
                  showOAuthMessage 
                    ? 'relative animate-bounce bg-yellow-100 px-3 py-1 rounded-lg border-2 border-yellow-400 shadow-lg' 
                    : ''
                }`}
                onClick={() => setShowModalRegistro(true)}
              >
                {showOAuthMessage && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full animate-ping">
                    !
                  </span>
                )}
                Registrarse
              </button>
            </div>
          </form>

          <Link
            to="/terminos-y-condiciones"
            className="absolute bottom-0 left-0 right-0 mb-5 mx-auto text-center text-[#205087ff] text-sm hover:underline transition-colors duration-200"
          >
            Redplika©2026 | <span className="hover:text-[#1f94c2ff]">Términos y condiciones</span>
          </Link>
        </div>
      </div>

      {/* Modal para recuperación de contraseña */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        className="w-full max-w-[420px] mx-auto bg-white rounded-xl sm:rounded-2xl shadow-2xl border-2 border-[#6e787aff] focus:outline-none max-h-[90vh] overflow-y-auto"
        overlayClassName="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50 p-4"
      >
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="text-center mb-4 sm:mb-6">
            <div className="mb-2 sm:mb-3">
              <span className="text-3xl sm:text-4xl">🔐</span>
            </div>
            <h2 className="text-xl sm:text-2xl text-[#1f94c2ff] font-black mb-1 sm:mb-2">
              Recuperación de contraseña
            </h2>
            <p className="text-xs sm:text-sm text-gray-600">
              {passwordResetStep === 1 && "Ingresa tu correo para recibir el código"}
              {passwordResetStep === 2 && "Ingresa el código de verificación"}
              {passwordResetStep === 3 && "Crea tu nueva contraseña"}
            </p>
            {/* Indicador de pasos */}
            <div className="flex items-center justify-center mt-3 space-x-2">
              <div className={`h-2 rounded-full transition-all duration-300 ${passwordResetStep >= 1 ? 'bg-[#1f94c2ff] w-8' : 'bg-gray-300 w-2'}`}></div>
              <div className={`h-2 rounded-full transition-all duration-300 ${passwordResetStep >= 2 ? 'bg-[#1f94c2ff] w-8' : 'bg-gray-300 w-2'}`}></div>
              <div className={`h-2 rounded-full transition-all duration-300 ${passwordResetStep >= 3 ? 'bg-[#1f94c2ff] w-8' : 'bg-gray-300 w-2'}`}></div>
            </div>
          </div>

          {/* Paso 1: Email */}
          {passwordResetStep === 1 && (
            <>
              <div className="mb-4 sm:mb-5">
                <label htmlFor="email" className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                  <span className="flex items-center">
                    <span className="mr-2">📧</span>
                    Correo electrónico
                  </span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[#1f94c2ff] focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                />
              </div>

              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={handlePasswordResetRequest}
                  className="w-full bg-gradient-to-r from-[#1f94c2ff] to-[#3090b3ff] border-2 border-[#1f94c2ff] text-white font-semibold px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl hover:from-[#3090b3ff] hover:to-[#1f94c2ff] hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
                >
                  <span className="flex items-center justify-center">
                    <span className="mr-2">✉️</span>
                    Solicitar Código
                  </span>
                </button>
                <button
                  onClick={handleCloseModal}
                  className="w-full bg-gray-200 text-gray-700 font-semibold px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl hover:bg-gray-300 transition-all duration-300"
                >
                  Cancelar
                </button>
              </div>
            </>
          )}

          {/* Paso 2: Código de verificación */}
          {passwordResetStep === 2 && (
            <>
              <div className="mb-4 sm:mb-5">
                <label htmlFor="email-display" className="block text-xs sm:text-sm text-gray-500 mb-1">
                  Correo: <span className="font-semibold text-gray-700">{email}</span>
                </label>
                <label htmlFor="verificationCode" className="block text-sm sm:text-base font-semibold text-gray-700 mb-2 mt-3">
                  <span className="flex items-center">
                    <span className="mr-2">🔑</span>
                    Código de verificación
                  </span>
                </label>
                <p className="text-xs sm:text-sm text-gray-500 mb-2">
                  Ingresa el código de 6 dígitos enviado a tu correo
                </p>
                <input
                  type="text"
                  id="verificationCode"
                  name="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  required
                  maxLength={6}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[#1f94c2ff] focus:ring-2 focus:ring-blue-100 transition-all duration-300 text-center text-lg font-semibold tracking-widest"
                />
              </div>

              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={handleVerifyCode}
                  disabled={!verificationCode || verificationCode.length !== 6}
                  className="w-full bg-gradient-to-r from-[#1f94c2ff] to-[#3090b3ff] border-2 border-[#1f94c2ff] text-white font-semibold px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl hover:from-[#3090b3ff] hover:to-[#1f94c2ff] hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <span className="flex items-center justify-center">
                    <span className="mr-2">✓</span>
                    Verificar Código
                  </span>
                </button>
                <button
                  onClick={() => {
                    setPasswordResetStep(1);
                    setVerificationCode('');
                  }}
                  className="w-full bg-gray-200 text-gray-700 font-semibold px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl hover:bg-gray-300 transition-all duration-300"
                >
                  Volver
                </button>
              </div>
            </>
          )}

          {/* Paso 3: Nueva contraseña */}
          {passwordResetStep === 3 && (
            <>
              <div className="mb-4 sm:mb-5">
                <label htmlFor="email-display" className="block text-xs sm:text-sm text-gray-500 mb-1">
                  Correo: <span className="font-semibold text-gray-700">{email}</span>
                </label>
                <label htmlFor="newPassword" className="block text-sm sm:text-base font-semibold text-gray-700 mb-2 mt-3">
                  <span className="flex items-center">
                    <span className="mr-2">🔒</span>
                    Nueva contraseña
                  </span>
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Ingresa tu nueva contraseña"
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[#1f94c2ff] focus:ring-2 focus:ring-blue-100 transition-all duration-300"
                />
              </div>

              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={handlePasswordReset}
                  disabled={!newPassword || newPassword.length < 8}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 border-2 border-green-400 text-white font-semibold px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl hover:from-green-600 hover:to-green-700 hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <span className="flex items-center justify-center">
                    <span className="mr-2">✓</span>
                    Cambiar Contraseña
                  </span>
                </button>
                <button
                  onClick={() => {
                    setPasswordResetStep(2);
                    setNewPassword('');
                  }}
                  className="w-full bg-gray-200 text-gray-700 font-semibold px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl hover:bg-gray-300 transition-all duration-300"
                >
                  Volver
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* MODAL PARA REGISTRO */}
      <Modal
        isOpen={showModalRegistro}
        onRequestClose={() => {
          setShowModalRegistro(false);
          setShowPrestadorOptions(false);
          setShowUsuarioOptions(false);
        }}
        className="w-full max-w-[420px] mx-auto bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-2xl text-center border-2 border-[#6e787aff] focus:outline-none max-h-[90vh] overflow-y-auto"
        overlayClassName="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50 p-4"
      >
        {!showPrestadorOptions && !showUsuarioOptions ? (
          <>
            <div className="mb-4 sm:mb-6">
              <div className="mb-2 sm:mb-3">
                <span className="text-3xl sm:text-4xl">👋</span>
              </div>
              <h2 className="text-xl sm:text-2xl text-[#1f94c2ff] tracking-wide font-black mb-1 sm:mb-2">
                ¿Cómo deseas registrarte?
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 px-2">
                Selecciona el tipo de cuenta que mejor se adapte a ti
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <button
                onClick={() => setShowPrestadorOptions(true)}
                className="w-full p-3 sm:p-4 md:p-5 bg-gradient-to-r from-[#1f94c2ff] to-[#3090b3ff] border-2 border-[#1f94c2ff] text-white font-semibold rounded-lg sm:rounded-xl hover:from-[#3090b3ff] hover:to-[#1f94c2ff] hover:border-[#3090b3ff] transition-all duration-300 transform hover:scale-[1.02] sm:hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                  <div className="bg-white bg-opacity-20 p-1.5 sm:p-2 md:p-2.5 rounded-full flex-shrink-0">
                    <span className="text-xl sm:text-2xl">👨‍⚕️</span>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-base sm:text-lg font-bold">Soy Profesional</div>
                    <div className="text-xs sm:text-sm opacity-90 font-normal">Prestadores de servicios de salud</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setShowUsuarioOptions(true)}
                className="w-full p-3 sm:p-4 md:p-5 bg-gradient-to-r from-[#f79865ff] to-[#ffa16f] border-2 border-[#f79865ff] text-white font-semibold rounded-lg sm:rounded-xl hover:from-[#ffa16f] hover:to-[#f79865ff] hover:border-[#ffa16f] transition-all duration-300 transform hover:scale-[1.02] sm:hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                  <div className="bg-white bg-opacity-20 p-1.5 sm:p-2 md:p-2.5 rounded-full flex-shrink-0">
                    <span className="text-xl sm:text-2xl">👤</span>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-base sm:text-lg font-bold">Soy Paciente</div>
                    <div className="text-xs sm:text-sm opacity-90 font-normal">Usuarios que buscan servicios de salud</div>
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={() => {
                setShowModalRegistro(false);
                setShowPrestadorOptions(false);
                setShowUsuarioOptions(false);
              }}
              className="w-full mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500 hover:text-gray-700 underline transition-colors duration-200"
            >
              Cancelar
            </button>
          </>
        ) : showPrestadorOptions ? (
          <>
            <button
              onClick={() => setShowPrestadorOptions(false)}
              className="absolute top-2 left-2 sm:top-4 sm:left-4 text-[#1f94c2ff] hover:text-[#3090b3ff] transition duration-300 font-semibold flex items-center space-x-1 text-sm sm:text-base"
            >
              <span>←</span>
              <span>Volver</span>
            </button>
            
            <div className="mb-4 sm:mb-6 mt-1 sm:mt-2">
              <h2 className="text-lg sm:text-xl text-[#1f94c2ff] tracking-wide font-black mb-1 sm:mb-2">
                ¿Qué tipo de membresía prefieres?
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 px-2">Elige la opción que mejor se adapte a tus necesidades</p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <button
                onClick={() => { navigate('/registro-gratuito') }}
                className="w-full p-3 sm:p-4 bg-gradient-to-r from-green-500 to-green-600 border-2 border-green-400 text-white font-semibold rounded-lg sm:rounded-xl hover:from-green-600 hover:to-green-700 hover:border-green-500 transition-all duration-300 transform hover:scale-[1.02] sm:hover:scale-105 shadow-lg"
              >
                <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                  <div className="bg-white bg-opacity-20 p-1.5 sm:p-2 rounded-full flex-shrink-0">
                    <span className="text-xl sm:text-2xl">🆓</span>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-base sm:text-lg font-bold">Registro Gratuito</div>
                    <div className="text-xs sm:text-sm opacity-90">Acceso básico sin costo</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => { navigate('/registro-pagado') }}
                className="w-full p-3 sm:p-4 bg-gradient-to-r from-[#1f94c2ff] to-[#3090b3ff] border-2 border-[#1f94c2ff] text-white font-semibold rounded-lg sm:rounded-xl hover:from-[#3090b3ff] hover:to-[#1f94c2ff] hover:border-[#3090b3ff] transition-all duration-300 transform hover:scale-[1.02] sm:hover:scale-105 shadow-lg"
              >
                <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                  <div className="bg-white bg-opacity-20 p-1.5 sm:p-2 rounded-full flex-shrink-0">
                    <span className="text-xl sm:text-2xl">💳</span>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-base sm:text-lg font-bold">Con Membresía</div>
                    <div className="text-xs sm:text-sm opacity-90">Acceso completo Premium</div>
                  </div>
                </div>
              </button>
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => setShowUsuarioOptions(false)}
              className="absolute top-2 left-2 sm:top-4 sm:left-4 text-[#f79865ff] hover:text-[#ffa16f] transition duration-300 font-semibold flex items-center space-x-1 text-sm sm:text-base"
            >
              <span>←</span>
              <span>Volver</span>
            </button>
            
            <div className="mb-4 sm:mb-6 mt-1 sm:mt-2">
              <h2 className="text-lg sm:text-xl text-[#f79865ff] tracking-wide font-black mb-1 sm:mb-2">
                ¿Qué tipo de membresía prefieres?
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 px-2">Elige la opción que mejor se adapte a tus necesidades</p>
            </div>

            <div className="space-y-3 sm:space-y-4">
            <button
              onClick={() => { navigate('/registro-gratuito-usuario') }}
              className="w-full p-3 sm:p-4 bg-gradient-to-r from-emerald-500 to-green-600 border-2 border-emerald-400 text-white font-semibold rounded-lg sm:rounded-xl hover:from-emerald-600 hover:to-green-700 hover:border-emerald-500 transition-all duration-300 transform hover:scale-[1.02] sm:hover:scale-105 shadow-lg"
            >
              <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                <div className="bg-white bg-opacity-20 p-1.5 sm:p-2 rounded-full flex-shrink-0">
                  <span className="text-xl sm:text-2xl">🎁</span>
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="text-base sm:text-lg font-bold">Registro Gratuito</div>
                  <div className="text-xs sm:text-sm opacity-90">Acceso básico para pacientes</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => { navigate('/registro-pagado-usuario') }}
              className="w-full p-3 sm:p-4 bg-gradient-to-r from-[#ff6b35] to-[#ff8c42] border-2 border-[#ff6b35] text-white font-semibold rounded-lg sm:rounded-xl hover:from-[#ff8c42] hover:to-[#ff6b35] hover:border-[#ff8c42] transition-all duration-300 transform hover:scale-[1.02] sm:hover:scale-105 shadow-lg relative"
            >
              <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-yellow-400 text-yellow-900 text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                ¡POPULAR!
              </div>
              <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                <div className="bg-white bg-opacity-20 p-1.5 sm:p-2 rounded-full flex-shrink-0">
                  <span className="text-xl sm:text-2xl">⭐</span>
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="text-base sm:text-lg font-bold">Con Membresía Premium</div>
                  <div className="text-xs sm:text-sm opacity-90">Beneficios exclusivos y aliados</div>
                </div>
              </div>
            </button>
            </div>
          </>
        )}

      </Modal>
    </div>
  );
};

export default Login;
